import React, { useEffect, useMemo, useState } from 'react';
import { transformHTMLParserLinks } from '../../../utils/rewriteLink/rewriteLink';

import { Container, Row, Col, Title } from '../../../importCL';
import { useApiTokenContext } from '../../../providers/ApiTokenProvider/ApiTokenProvider';
import { isAuthorMode, wrapTrademarkSuperscript } from '../../../utils/cob-utils';
import {
    IYoutubeResourceIdBean,
    YoutubeItemWithDescriptionOverride,
} from '../../../utils/youtube-utils/YoutubeTypes/YoutubeTypes';
import {
    findVideoId,
    initialiseAndUpdateYoutubeVideoCache,
    YoutubeConstants,
} from '../../../utils/youtube-utils/youtube-utils';
import { LinkWrapper } from '../../LinkWrapper/LinkWrapper';
import { IVideoPlaylistProps } from '../VideoPlaylist';
import VideoCarousel, { IVideoSlideProps, VideoPlaceholder, VideoSlide } from './NewVideoCarousel';
import './VideoPlaylistCarouselVariant.css';

const EMPTY_CACHE: Record<string, YoutubeItemWithDescriptionOverride> = {};
const SINGLE_SLIDE_STYLES = { width: '100%' };

const parseAuthoredText = (text?: string) => (
    text?.trim() ? transformHTMLParserLinks(wrapTrademarkSuperscript(text)) : null
);

const getPlaceholderCount = (youtubeBeans: IYoutubeResourceIdBean[]) => (
    youtubeBeans.length === 1 && !youtubeBeans[0].youtubePlaylistToggle
        ? 1
        : Math.max(youtubeBeans.length, 3)
);

const getYoutubeResourceId = (bean: IYoutubeResourceIdBean): string => {
    const carouselItem = bean.youtubeResourceId?.trim() || '';
    if (!carouselItem) {
        return carouselItem;
    }

    if (bean.youtubePlaylistToggle) {
        const query = carouselItem.split('?')[1];
        return new URLSearchParams(query).get('list') || carouselItem;
    }

    return findVideoId(carouselItem)?.[1] || carouselItem;
};

const usePlaylistVideos = (youtubeBeans?: IYoutubeResourceIdBean[]) => {
    const [videoCache, setVideoCache] = useState(EMPTY_CACHE);
    const { getTokenString } = useApiTokenContext();

    useEffect(() => {
        const apiKey = getTokenString(YoutubeConstants.key);
        let isCurrent = true;

        if (apiKey && youtubeBeans?.length) {
            initialiseAndUpdateYoutubeVideoCache(youtubeBeans, apiKey)
                .then((cache) => isCurrent && setVideoCache(cache))
                .catch(() => isCurrent && setVideoCache(EMPTY_CACHE));
        } else {
            setVideoCache(EMPTY_CACHE);
        }

        return () => {
            isCurrent = false;
        };
    }, [ youtubeBeans, getTokenString ]);

    return Object.entries(videoCache)
        .filter(([, video]) => Boolean(video?.snippet))
        .map(([videoId, video]) => ({ ...video, id: videoId }));
};

const DefaultDisclaimer = () => (
    <p>
        Credit One Bank uses YouTube API services and by watching the video, you are agreeing to the
        YouTube Terms of Service (<LinkWrapper target='_self' href='https://youtube.com/terms'>
            https://youtube.com/terms
        </LinkWrapper>) and Google Privacy Policy (<LinkWrapper
            target='_self'
            href='https://www.google.com/policies/privacy'
        >
            https://www.google.com/policies/privacy
        </LinkWrapper>).
    </p>
);

const VideoPlaylistCarousel = (props: IVideoPlaylistProps) => {
    const youtubeBeans = useMemo<IYoutubeResourceIdBean[]>(() => (
        (props.youtubeBeans || []).map((bean): IYoutubeResourceIdBean => ({
            ...bean,
            youtubeResourceId: getYoutubeResourceId(bean),
        }))
    ), [props.youtubeBeans]);
    const videos = usePlaylistVideos(youtubeBeans);
    const showAuthoredPreviews = props.isInEditor ||  isAuthorMode();
    const slideCount = showAuthoredPreviews ? getPlaceholderCount(youtubeBeans) : videos.length;
    const isSingle = slideCount < 3;
    const commonSlideProps: IVideoSlideProps = {
        isActive: isSingle,
        styles: isSingle ? SINGLE_SLIDE_STYLES : undefined,
    };
    const slides = Array.from({ length: slideCount }, (_, index) => {
        const authoredVideo = youtubeBeans[index];
        const thumbnail = authoredVideo?.youtubeThumbnailOverridePath?.trim();
        const youtubeId = authoredVideo?.youtubeResourceId?.trim();
        const youtubeEmbedURL = youtubeId
            ? authoredVideo.youtubePlaylistToggle
                ? `https://www.youtube-nocookie.com/embed/videoseries?list=${encodeURIComponent(youtubeId)}&autoplay=1&enablejsapi=1`
                : `https://www.youtube-nocookie.com/embed/${encodeURIComponent(youtubeId)}?rel=0&autoplay=1&enablejsapi=1`
            : undefined;

        return showAuthoredPreviews ? (
            <VideoPlaceholder
                {...commonSlideProps}
                key={'video-placeholder-' + index}
                id={'video-' + index}
                thumbnail={thumbnail}
                thumbnailAlt={authoredVideo?.youtubeDescriptionOverride}
                youtubeEmbedURL={youtubeEmbedURL}
            />
        ) : (
            <VideoSlide
                {...commonSlideProps}
                key={videos[index].id}
                id={'video-' + index}
                cachedData={videos[index]}
            />
        );
    });
    const content = slideCount >= 3
        ? <VideoCarousel id='video-playlist-carousel' className='PlaylistCarousel' slides={slides} />
        : slides[0] || null;

    return (
        <Container className='VideoPlaylist VideoPlaylistCarousel'>
            <Row>
                <Col>
                    {props.heading && (
                        <Title
                            tag='h2'
                            spanTitle={parseAuthoredText(props.subHeading)}
                            showHr
                            verticalBar={!props.centeredHeader}
                            align={props.centeredHeader ? 'center' : 'left'}
                        >
                            {parseAuthoredText(props.heading)}
                        </Title>
                    )}
                </Col>
            </Row>
            <Row className='mt-4 md:mt-8'>
                <Col>{content}</Col>
            </Row>
            <Row className='YoutubeDisclaimer'>
                <Col>
                    {parseAuthoredText(props.videoDisclaimer) || <DefaultDisclaimer />}
                </Col>
            </Row>
        </Container>
    );
};

export default VideoPlaylistCarousel;
